import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, EnvelopeIcon, UserIcon, StarIcon, CurrencyPoundIcon, ChevronLeftIcon, LinkIcon } from '@heroicons/react/24/outline';
import PublicHeader from '../components/PublicHeader';
import config from '../config';

const apiBaseUrl = config.apiBaseUrl;

// Base URLs — stored values are usernames only
const SOCIAL_BASES = {
    tikTokUrl:    'https://tiktok.com/@',
    blueskyUrl:   'https://bsky.app/profile/',
    instagramUrl: 'https://instagram.com/',
    facebookUrl:  'https://facebook.com/',
    stravaUrl:    'https://strava.com/athletes/',
    linkedInUrl:  'https://linkedin.com/in/',
};

/** Build a full URL from a stored username. If the value already looks like a URL, return as-is. */
const socialUrl = (key, username) => {
    if (!username) return null;
    if (username.startsWith('http://') || username.startsWith('https://')) return username;
    return `${SOCIAL_BASES[key]}${username}`;
};

// Simple SVG icons for social platforms
const TikTokIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"/>
    </svg>
);

const BlueskyIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 01-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.204-.659-.299-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/>
    </svg>
);

const InstagramIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
);

const FacebookIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const StravaIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.08-7.51l2.09 4.109h4.136L9.592 0 4.44 10.631h4.137"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans',       label: 'Diet / Meal Plans' },
    { key: 'sports_massage',        label: 'Sports Massage' },
    { key: 'consultations',         label: 'Consultations' },
    { key: 'online_coaching',       label: 'Online Coaching' },
    { key: 'group_sessions',        label: 'Group Sessions' },
    { key: 'nutrition_advice',      label: 'Nutrition Advice' },
    { key: 'body_composition',      label: 'Body Composition Testing' },
    { key: 'rehabilitation',        label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal',        label: 'Pre / Post Natal' },
    { key: 'mindset_coaching',      label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning', label: 'Strength & Conditioning' },
    { key: 'youth_training',        label: 'Youth / Junior Training' },
];

const formatPrice = (pt) => {
    if (!pt.priceType || !pt.priceFrom) return null;
    if (pt.priceType === 'from') return `From £${pt.priceFrom}`;
    if (pt.priceType === 'range' && pt.priceTo) return `£${pt.priceFrom} – £${pt.priceTo}`;
    return `From £${pt.priceFrom}`;
};

/** OpenStreetMap embed via Nominatim geocoding + iframe tile layer */
const PtMap = ({ line1, line2, city, postcode }) => {
    const [coords, setCoords] = useState(null);
    // Build the most-specific query possible for an accurate pin
    const locationQuery = [line1, line2, city, postcode].filter(Boolean).join(', ');

    useEffect(() => {
        if (!locationQuery) return;
        fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationQuery)}&format=json&limit=1`, {
            headers: { 'Accept-Language': 'en' }
        })
            .then(r => r.json())
            .then(data => {
                if (data && data.length > 0) {
                    setCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) });
                }
            })
            .catch(() => {/* ignore map errors */});
    }, [locationQuery]);

    if (!coords) return null;

    const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${coords.lon - 0.05},${coords.lat - 0.05},${coords.lon + 0.05},${coords.lat + 0.05}&layer=mapnik&marker=${coords.lat},${coords.lon}`;

    return (
        <div className="rounded-lg overflow-hidden border border-gray-700 h-64 w-full">
            <iframe
                title={`Map for ${locationQuery}`}
                src={mapSrc}
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0 }}
                allowFullScreen
            />
        </div>
    );
};

const ContactModal = ({ pt, onClose }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        setStatus('');
        try {
            const response = await fetch(`${apiBaseUrl}/api/ptdirectory/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ptDirectoryListingId: pt.id,
                    enquirerEmail: email.trim(),
                    enquirerName: name.trim() || null,
                    message: message.trim() || null
                })
            });
            setStatus(response.ok ? 'success' : 'error');
        } catch {
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-semibold text-white mb-2">Contact {pt.name}</h3>
                <p className="text-gray-400 text-sm mb-4">
                    Fill in your details below and {pt.name} will get back to you.
                </p>

                {status === 'success' ? (
                    <div className="text-center py-4">
                        <div className="text-emerald-400 text-4xl mb-3">✓</div>
                        <p className="text-emerald-300 font-medium mb-1">Enquiry sent!</p>
                        <p className="text-gray-400 text-sm">{pt.name} will be in touch soon.</p>
                        <button
                            onClick={onClose}
                            className="mt-4 bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Your Name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Jane Smith"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Your Email Address *</label>
                            <input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                placeholder="jane@example.com"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Message (optional)</label>
                            <textarea
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                rows={3}
                                placeholder="Tell the PT a bit about your goals…"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                            />
                        </div>

                        {status === 'error' && (
                            <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !email.trim()}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-medium transition-colors"
                            >
                                {loading ? 'Sending…' : 'Send Enquiry'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const PTProfile = () => {
    const { slug } = useParams();
    const [pt, setPt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [showContact, setShowContact] = useState(false);

    useEffect(() => {
        const fetchListing = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/api/ptdirectory/${slug}`);
                if (response.status === 404) {
                    setNotFound(true);
                } else if (response.ok) {
                    const data = await response.json();
                    setPt(data.listing);
                } else {
                    setNotFound(true);
                }
            } catch {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };
        fetchListing();
    }, [slug]);

    // SEO: update document title and meta tags dynamically
    useEffect(() => {
        if (!pt) return;
        const ptServices = pt.services
            ? pt.services.split(',').map(s => {
                const svc = SERVICE_OPTIONS.find(o => o.key === s.trim());
                return svc ? svc.label : s.trim();
            }).join(', ')
            : '';

        document.title = `${pt.name} – Personal Trainer in ${pt.city || pt.postcode || 'UK'} | WorldsHighStreet`;

        const setMeta = (name, content, property) => {
            let el = property
                ? document.querySelector(`meta[property="${property}"]`)
                : document.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement('meta');
                if (property) el.setAttribute('property', property);
                else el.setAttribute('name', name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        const description = pt.bio
            ? `${pt.name} is a personal trainer based in ${pt.city || pt.postcode || 'the UK'}. ${pt.bio.substring(0, 150)}`
            : `${pt.name} is a personal trainer based in ${pt.city || pt.postcode || 'the UK'}${ptServices ? `, specialising in ${ptServices}` : ''}. Get in touch via the WorldsHighStreet PT Directory.`;

        setMeta('description', description);
        setMeta('keywords', `personal trainer ${pt.city}, PT ${pt.city}, ${pt.name}, ${ptServices}`);
        setMeta(null, description, 'og:description');
        setMeta(null, `${pt.name} – Personal Trainer in ${pt.city || pt.postcode || 'UK'}`, 'og:title');
        setMeta(null, 'website', 'og:type');
        setMeta('robots', 'index, follow');

        return () => {
            document.title = 'WorldsHighStreet';
        };
    }, [pt]);

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
            </div>
        );
    }

    if (notFound || !pt) {
        return (
            <div className="min-h-screen bg-black">
                <PublicHeader title="Personal Trainer Not Found" subtitle="" showBackToHome={true} />
                <main className="max-w-2xl mx-auto px-4 py-16 text-center">
                    <UserIcon className="h-20 w-20 text-gray-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
                    <p className="text-gray-400 mb-6">This personal trainer's profile could not be found or may have been removed.</p>
                    <Link to="/find-pt" className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                        Browse All Trainers
                    </Link>
                </main>
            </div>
        );
    }

    const priceDisplay = formatPrice(pt);
    const ptServices = pt.services
        ? pt.services.split(',').map(s => s.trim()).filter(Boolean)
        : [];
    const ptSpecialisms = pt.specialisms
        ? pt.specialisms.split(',').map(s => s.trim()).filter(Boolean)
        : [];

    return (
        <div className="min-h-screen bg-black">
            {/* SEO-friendly header */}
            <PublicHeader
                title={`${pt.name}`}
                subtitle={[pt.line1, pt.line2, pt.city, pt.postcode].filter(Boolean).join(', ')}
                showBackToHome={false}
            />

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back to directory */}
                <Link
                    to="/find-pt"
                    className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-6 transition-colors"
                >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Back to PT Directory
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left: Main profile */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Profile card */}
                        <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="w-16 h-16 bg-emerald-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <UserIcon className="h-8 w-8 text-emerald-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-2xl font-bold text-white leading-tight">{pt.name}</h1>
                                    {pt.isFounderMember && (
                                        <span className="inline-flex items-center gap-1 text-sm text-yellow-400 font-medium mt-1">
                                            <StarIcon className="h-4 w-4" />
                                            Foundation Member
                                        </span>
                                    )}
                                    <div className="flex items-center text-gray-400 text-sm mt-1">
                                        <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                        <span>
                                            {[pt.line1, pt.line2, pt.city, pt.postcode].filter(Boolean).join(', ')}
                                        </span>
                                    </div>
                                    {priceDisplay && (
                                        <div className="flex items-center text-emerald-400 text-sm font-medium mt-1">
                                            <CurrencyPoundIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                            {priceDisplay} per session
                                        </div>
                                    )}
                                </div>
                            </div>

                            {pt.bio && (
                                <div className="mb-4">
                                    <h2 className="text-lg font-semibold text-white mb-2">About Me</h2>
                                    <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{pt.bio}</p>
                                </div>
                            )}

                            {ptSpecialisms.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-lg font-semibold text-white mb-2">Specialisms</h2>
                                    <div className="flex flex-wrap gap-2">
                                        {ptSpecialisms.map((s, i) => (
                                            <span key={i} className="text-sm bg-emerald-900/30 text-emerald-300 border border-emerald-800/50 px-3 py-1 rounded-full">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {ptServices.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-semibold text-white mb-2">Services Offered</h2>
                                    <div className="flex flex-wrap gap-2">
                                        {ptServices.map(key => {
                                            const svc = SERVICE_OPTIONS.find(s => s.key === key);
                                            if (!svc) return null;
                                            return (
                                                <span key={key} className="text-sm bg-blue-900/20 text-blue-300 border border-blue-800/40 px-3 py-1 rounded-full">
                                                    {svc.label}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Map */}
                        <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
                            <h2 className="text-lg font-semibold text-white mb-3">Location</h2>
                            <PtMap line1={pt.line1} line2={pt.line2} city={pt.city} postcode={pt.postcode} />
                            <div className="flex items-start gap-1.5 text-gray-400 text-sm mt-3">
                                <MapPinIcon className="inline h-4 w-4 mr-0.5 mt-0.5 flex-shrink-0" />
                                <span>
                                    {[pt.line1, pt.line2, pt.city, pt.postcode]
                                        .filter(Boolean)
                                        .join(', ')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Contact card (sticky on desktop) */}
                    <div className="lg:col-span-1">
                        <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 sticky top-6">
                            <h2 className="text-lg font-semibold text-white mb-3">Get in Touch</h2>
                            <p className="text-gray-400 text-sm mb-4">
                                Interested in training with {pt.name}? Send them a message and they'll get back to you.
                            </p>
                            <button
                                onClick={() => setShowContact(true)}
                                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
                            >
                                <EnvelopeIcon className="h-5 w-5" />
                                Contact {pt.name}
                            </button>

                            {pt.beaconsUsername && (
                                <a
                                    href={`https://beacons.ai/${pt.beaconsUsername}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                                >
                                    <LinkIcon className="h-5 w-5 text-emerald-400" />
                                    View Services & Offers
                                </a>
                            )}

                            {(pt.tikTokUrl || pt.blueskyUrl || pt.instagramUrl || pt.facebookUrl || pt.stravaUrl || pt.linkedInUrl) && (
                                <div className="mt-4 pt-4 border-t border-gray-700">
                                    <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Follow on Social Media</p>
                                    <div className="flex flex-wrap gap-2">
                                        {[
                                            { key: 'tikTokUrl',    label: 'TikTok',    Icon: TikTokIcon,    colorClass: 'bg-gray-800 hover:bg-gray-700 text-white' },
                                            { key: 'blueskyUrl',   label: 'Bluesky',   Icon: BlueskyIcon,   colorClass: 'bg-sky-900/50 hover:bg-sky-800/60 text-sky-300' },
                                            { key: 'instagramUrl', label: 'Instagram', Icon: InstagramIcon, colorClass: 'bg-pink-900/40 hover:bg-pink-800/50 text-pink-300' },
                                            { key: 'facebookUrl',  label: 'Facebook',  Icon: FacebookIcon,  colorClass: 'bg-blue-900/40 hover:bg-blue-800/50 text-blue-300' },
                                            { key: 'stravaUrl',    label: 'Strava',    Icon: StravaIcon,    colorClass: 'bg-orange-900/40 hover:bg-orange-800/50 text-orange-300' },
                                            { key: 'linkedInUrl',  label: 'LinkedIn',  Icon: LinkedInIcon,  colorClass: 'bg-blue-900/50 hover:bg-blue-800/60 text-blue-200' },
                                        ].filter(s => pt[s.key]).map(({ key, label, Icon, colorClass }) => (
                                            <a
                                                key={label}
                                                href={socialUrl(key, pt[key])}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${colorClass}`}
                                                title={label}
                                            >
                                                <Icon />
                                                <span>{label}</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 pt-4 border-t border-gray-700">
                                <p className="text-xs text-gray-500 text-center">
                                    Listed on the <Link to="/find-pt" className="text-emerald-400 hover:underline">WorldsHighStreet PT Directory</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {showContact && (
                <ContactModal pt={pt} onClose={() => setShowContact(false)} />
            )}
        </div>
    );
};

export default PTProfile;
